import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { addressError, fetchDemo, fetchToken } from "../lib/trace";
import { TokenSearch } from "../components/trace/TokenSearch";
import { DemoBanner, ScanStatusBar } from "../components/scanner/ScanStatusBar";
import { OverviewCard, RiskSummary } from "../components/scanner/Overview";
import { MarketStats } from "../components/scanner/MarketStats";
import { HolderDistribution } from "../components/scanner/HolderDistribution";
import { AuthorityPanel, LiquidityPanel } from "../components/scanner/Liquidity";
import { WalletActivity } from "../components/scanner/WalletActivity";
import { RiskIndicators } from "../components/scanner/RiskIndicators";
import { DataLimitations, EmptyState, ErrorState, LoadingState } from "../components/scanner/States";

const Report = ({ data, onRefresh, refreshing }) => (
  <div className="space-y-4" data-testid="scan-report">
    {data.is_demo && <DemoBanner />}
    <ScanStatusBar data={data} onRefresh={onRefresh} refreshing={refreshing} />
    <div className="grid gap-4 lg:grid-cols-3">
      <OverviewCard data={data} />
      <RiskSummary data={data} />
    </div>
    <MarketStats data={data} />
    <div className="grid gap-4 lg:grid-cols-3">
      <HolderDistribution data={data} />
      <AuthorityPanel data={data} />
      <LiquidityPanel data={data} />
      <WalletActivity data={data} />
      <RiskIndicators data={data} />
    </div>
    {data.is_demo && <DemoBanner />}
    <DataLimitations />
  </div>
);

export default function Scanner() {
  const { address } = useParams();
  const [state, setState] = useState({ status: "idle" });

  const load = useCallback(async (refresh = false) => {
    if (!address) return setState({ status: "idle" });
    const isDemo = address === "demo";
    const err = isDemo ? null : addressError(address);
    if (err) return setState({ status: "error", error: err, retryable: false });
    setState((s) => (refresh && s.data ? { ...s, refreshing: true } : { status: "loading" }));
    try {
      const data = isDemo ? await fetchDemo() : await fetchToken(address, refresh);
      setState({ status: "ready", data });
      document.title = `${data.is_demo ? "Demo report" : `${data.token.symbol} token scan`} — TRACE`;
    } catch (e) {
      const code = e.response?.status;
      const msg = e.response?.data?.detail || "Network error: could not reach the TRACE scanner. Check your connection and try again.";
      if (refresh) {
        toast.error(`Refresh failed: ${msg}`);
        return setState((s) => ({ ...s, refreshing: false }));
      }
      setState({
        status: "error",
        error: msg,
        retryable: !code || code >= 500,
      });
    }
  }, [address]);

  useEffect(() => {
    document.title = "Token Scanner — TRACE";
    load();
  }, [load]);

  return (
    <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="scanner-page">
      <div className="mb-8 grid gap-6 lg:grid-cols-[1fr_1.6fr] lg:items-end">
        <div>
          <p className="eyebrow">Token scanner</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-[-0.02em] text-white sm:text-4xl">Trace a Solana token</h1>
        </div>
        <TokenSearch key={address} initial={address && address !== "demo" ? address : ""} size="sm" showSamples={false} />
      </div>
      {state.status === "idle" && <EmptyState />}
      {state.status === "loading" && <LoadingState />}
      {state.status === "error" && <ErrorState message={state.error} retryable={state.retryable} onRetry={() => load()} />}
      {state.status === "ready" && <Report data={state.data} refreshing={state.refreshing} onRefresh={() => load(true)} />}
    </main>
  );
}
